import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RotateCcw } from "lucide-react";
import useImageModelStore from "@/store/imageModelStore";

const ImageModelParameters = () => {
  const {
    selectedModel,
    getSelectedModel,
    getCurrentModelParameters,
    updateModelParameter,
    resetCurrentModelParameters,
  } = useImageModelStore();

  const model = getSelectedModel();
  const parameters = getCurrentModelParameters();

  if (!model || !model.parameters) return null;

  const renderParameter = (paramName, paramConfig) => {
    const value = parameters[paramName];

    switch (paramConfig.type) {
      case "select":
        return (
          <div key={paramName} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{paramConfig.label}</Label>
            </div>
            <Select
              value={String(value)}
              onValueChange={(newValue) =>
                updateModelParameter(selectedModel, paramName, newValue)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paramConfig.options.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {paramConfig.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {paramConfig.description}
              </p>
            )}
          </div>
        );

      case "number": {
        const numberValue = Number(value);
        return (
          <div key={paramName} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{paramConfig.label}</Label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {numberValue}
              </span>
            </div>
            <Slider
              value={[numberValue]}
              onValueChange={([newValue]) =>
                updateModelParameter(selectedModel, paramName, newValue)
              }
              min={paramConfig.min || 0}
              max={paramConfig.max || 100}
              step={paramConfig.step || 1}
              className="w-full"
            />
            {paramConfig.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {paramConfig.description}
              </p>
            )}
          </div>
        );
      }

      case "text": {
        return (
          <div key={paramName} className="space-y-2">
            <Label className="text-sm font-medium">{paramConfig.label}</Label>
            <Textarea
              value={value || ""}
              onChange={(e) =>
                updateModelParameter(selectedModel, paramName, e.target.value)
              }
              placeholder={paramConfig.description}
              className="min-h-[80px] resize-none"
            />
            {paramConfig.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {paramConfig.description}
              </p>
            )}
          </div>
        );
      }

      case "boolean": {
        return (
          <div key={paramName} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{paramConfig.label}</Label>
              <Switch
                checked={Boolean(value)}
                onCheckedChange={(checked) =>
                  updateModelParameter(selectedModel, paramName, checked)
                }
              />
            </div>
            {paramConfig.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {paramConfig.description}
              </p>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const paramCount = Object.keys(model.parameters).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Parameters
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetCurrentModelParameters}
          className="h-8 px-2"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          Reset
        </Button>
      </div>

      {paramCount > 0 ? (
        <div className="space-y-4">
          {Object.entries(model.parameters).map(([paramName, paramConfig]) =>
            renderParameter(paramName, paramConfig)
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This model has no configurable parameters.
        </p>
      )}

      {/* Model limits info */}
      {model.limits && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            {model.limits.maxImages && (
              <div>
                <span className="font-medium">Max images:</span>{" "}
                {model.limits.maxImages}
              </div>
            )}
            {model.limits.maxPromptLength && (
              <div>
                <span className="font-medium">Max prompt:</span>{" "}
                {model.limits.maxPromptLength} characters
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageModelParameters;
